import React, { useEffect } from "react";
import { X, MapPin, ShoppingCart, Package, CheckCircle2, Truck } from "lucide-react";
import type { CheckoutModalProps } from "./types";

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  show,
  cartItems,
  addresses,
  selectedAddress,
  hasAddresses,
  itemsTotal,
  itemsDiscount,
  shippingCost,
  estimatedTotal,
  isLoading,
  onClose,
  onSelectAddress,
  onConfirmOrder,
  onContinueShopping,
}) => {
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  if (!show) return null;

  const formatCurrency = (amount: number) =>
    `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-section-card-bg rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-section-border animate-in slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative overflow-hidden bg-primary p-6">
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Package className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-primary-foreground">Checkout</h2>
                <p className="text-sm text-primary-foreground/70 mt-1">One last step to complete your order</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-white/10 rounded-xl transition-all duration-200"
            >
              <X className="w-6 h-6 text-primary-foreground" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-6 space-y-6">
            {/* Address Selection */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3 text-section-text">
                <div className="w-10 h-10 bg-section-surface border border-section-border rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                Shipping Address
              </h3>

              {hasAddresses ? (
                <div className="space-y-3">
                  {addresses.map((address) => (
                    <div
                      key={address.id}
                      onClick={() => onSelectAddress(address)}
                      className={`relative group p-5 border rounded-2xl cursor-pointer transition-all duration-200 ${
                        selectedAddress?.id === address.id
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "border-section-border hover:border-primary/30 hover:shadow-sm bg-section-surface"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-bold text-section-text text-lg mb-1">
                            {address.address_type_value || "Address"}
                          </div>
                          <div className="text-sm text-section-text font-medium">{address.street}</div>
                          <div className="text-sm text-section-muted mt-1">{address.city}</div>
                          {address.building && (
                            <div className="text-xs text-section-muted mt-1">
                              Building {address.building}
                              {address.floor && `, Floor ${address.floor}`}
                              {address.apartment && `, Apt ${address.apartment}`}
                            </div>
                          )}
                        </div>
                        {selectedAddress?.id === address.id && (
                          <div className="flex items-center justify-center">
                            <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center shadow-sm">
                              <CheckCircle2 className="w-4 h-4 text-primary-foreground" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 border-2 border-dashed border-section-border rounded-2xl bg-section-surface">
                  <MapPin className="w-14 h-14 text-section-muted mx-auto mb-3" />
                  <p className="text-section-text font-medium">No addresses available</p>
                  <p className="text-sm text-section-muted mt-1">Please add a shipping address</p>
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3 text-section-text">
                <div className="w-10 h-10 bg-section-surface border border-section-border rounded-xl flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-primary" />
                </div>
                Order Summary
                <span className="text-sm font-semibold text-section-muted bg-section-surface border border-section-border px-3 py-1 rounded-full">
                  {cartItems.length} {cartItems.length === 1 ? "item" : "items"}
                </span>
              </h3>

              <div className="bg-section-surface rounded-2xl p-4 max-h-64 overflow-y-auto border border-section-border">
                <div className="space-y-3">
                  {cartItems.map((item) => (
                    <div key={item.cart_key} className="flex items-center gap-4 bg-section-card-bg p-3 rounded-xl border border-section-border">
                      <div className="relative">
                        <img
                          src={item.image || "/api/placeholder/60/60"}
                          alt={item.name}
                          className="w-14 h-14 object-cover rounded-lg"
                        />
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center shadow-sm">
                          {item.cart_quantity}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-section-text truncate">{item.name}</div>
                        {item.variant_options && Object.keys(item.variant_options).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {Object.entries(item.variant_options).map(([type, value]) => (
                              <span key={type} className="text-xs bg-section-surface border border-section-border text-section-text px-1.5 py-0.5 rounded-full">
                                <span className="text-section-muted">{type}:</span> {value}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="text-xs text-section-muted mt-0.5">
                          {formatCurrency(item.usd_price || 0)} × {item.cart_quantity}
                        </div>
                      </div>
                      <div className="text-base font-bold text-section-text">
                        {formatCurrency((item.usd_price || 0) * item.cart_quantity)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="bg-section-surface rounded-2xl p-6 border border-section-border">
              <div className="space-y-3">
                {itemsDiscount > 0 && (
                  <>
                    <div className="flex justify-between text-base">
                      <span className="text-section-muted">Items total</span>
                      <span className="line-through text-section-muted font-medium">{formatCurrency(itemsTotal)}</span>
                    </div>
                    <div className="flex justify-between text-base">
                      <span className="text-green-600 font-medium">Discount</span>
                      <span className="text-green-600 font-bold">-{formatCurrency(itemsDiscount)}</span>
                    </div>
                  </>
                )}
                {shippingCost > 0 && (
                  <div className="flex justify-between text-base items-center">
                    <span className="text-section-muted flex items-center gap-2">
                      <Truck className="w-4 h-4 text-primary" />
                      Shipping
                    </span>
                    <span className="font-semibold text-section-text">{formatCurrency(shippingCost)}</span>
                  </div>
                )}
                <div className={itemsDiscount > 0 || shippingCost > 0 ? "border-t border-section-border pt-3" : ""}>
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-section-text">Total</span>
                    <span className="text-2xl font-bold text-section-text">{formatCurrency(estimatedTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={onContinueShopping}
                className="flex-1 border border-section-border text-section-muted py-4 rounded-xl font-bold hover:bg-section-surface hover:border-primary/30 transition-all duration-200"
              >
                Continue Shopping
              </button>
              <button
                onClick={onConfirmOrder}
                disabled={isLoading || !selectedAddress || !selectedAddress.id}
                className="flex-1 bg-primary text-primary-foreground py-4 rounded-xl font-bold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-[0_4px_14px_rgba(201,169,110,0.2)] hover:-translate-y-0.5 relative overflow-hidden group"
              >
                <span className="relative z-10">
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></div>
                      Processing...
                    </span>
                  ) : (
                    "Confirm Order"
                  )}
                </span>
                <div className="absolute inset-0 bg-white/10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
